<style lang="scss" scoped>
.classes {
	& ::v-deep .el-select {
		width: 100%;
	}
	&__container {
		@include container;
	}
	&__panel {
		@include main-panel;
	}
	&__header {
		@include flex-center;
		&__options {
			margin-left: auto;
			> div {
				margin-right: 15px;
				font-size: 100%;
			}
		}
	}
	&__footer {
		@include main-panel-footer;
		&__right {
			margin-left: auto;
		}
	}
	&__warning {
		color: red;
		font-size: 20px;
		padding-top: 7px;
	}
	.button {
		margin-right: 5px;
	}
	.toggle-button {
		margin-right: 15px;
	}
	.progress-bar {
		margin: 40px 0px 0px 0px;
	}
}
</style>
<template lang="pug">
.classes
	app-header
		.classes__header(slot='header')
			h1 Match classes
			.classes__header__options
				auto-enable-classes-button.toggle-button(ref="classEnableBtn", buttonLabel="automated classes sync", @auto-sync-classes-change="refetchMathedClasses")
				el-dropdown(@command="createIgnore").ml-auto
					el-button(type="plain") Toggle All
						i(class="el-icon-arrow-down el-icon--right")
					el-dropdown-menu(slot="dropdown")
						el-dropdown-item(command="create_on_sync") Create All on Sync
						el-dropdown-item(command="ignore") Ignore All on Sync
				el-dropdown(@command="actionCommand")
					el-button(type="primary", :loading="actionLoading")
						| Action
						i.el-icon-arrow-down.el-icon--right
					el-dropdown-menu(slot="dropdown")
						el-dropdown-item(command="add_admin", v-if="providerCode === 'gapps'") Add admin to all classrooms
						el-dropdown-item(command="archive") Archive all matched classes
						el-dropdown-item(command="refetch") Refetch classes
						el-dropdown-item(command="create_group", v-if="providerCode === 'gapps' && groupsInput.length > 0") Create group{{ groupsInput.length > 1 ? 's' : '' }}
						el-dropdown-item(command="save") {{ saveText}}
	.classes__container
		.classes__panel
			loading(v-if="$apollo.loading || saveLoading || classesLoading || wondeClassesLoading")
			template(v-else)
				div(v-if="addAdminLoading")
						loading(:message="addAdminProgressMessages")
							el-progress.progress-bar(:percentage="addAdminProgress", :text-inside="true", :stroke-width="18", text-color="#fff")
				div(v-else)
					el-alert(v-if="classSettings && classSettings.class_create_mode === 'self' && providerCode === 'gapps'" type="warning" center show-icon :closable="false" title="Class creation is not enabled" :description="`To allow classes to be automatically created during a sync, see Settings > ${providerName} Classroom`")
					result-filter.ml-auto.mr-2(v-model="filters" :filter-fields='fields', :remember="false")
					el-table(:data="paginated", @selection-change="handleSelectionChange").custom-table.overflow
						el-table-column(width="80", type="selection", v-if="providerCode === 'gapps'")
						el-table-column(prop="name" label="Edusync class")    
							template(slot-scope="scope") 
								span {{ scope.row.name }}
						el-table-column(prop="code" label="Code")    
							template(slot-scope="scope") 
								span {{ scope.row.code }}
						el-table-column(prop="type" label="Type")
							template(slot-scope="scope") 
								span {{ scope.row.type | capitalize }}
						el-table-column(width="50")
							template(slot-scope="scope")
								.classes__warning(v-if="rowDuplicated(scope.row)")
									i.fe.fe-alert-triangle
								.classes__warning(v-if="tooManyCreateNows(scope.row)")
									i.fe.fe-alert-triangle
						el-table-column(:label="`${providerName} Class`" width="500")
							template(slot-scope="scope")
								el-tooltip(effect="dark" :content="`Type to search an existing ${providerName} class`" placement="left")
									el-select(placeholder='Manage', filterable, v-model="scope.row.assigned", @change='change', :filter-method="classesFilter", allowCreate)
										el-option-group
											el-option(key="ignore" value="ignore" label="Ignore")
											el-option(key="create_later" value="create_on_sync" label="Create on sync")
											el-option(key="create" value="create" label="Create now")
											el-option(key="archive" value="archive" label="Archive class")
										el-option-group(:label="`Match to an existing ${providerName} class`")
											el-option(v-for="cls in getTPClassOptions(scope.row)" :key="cls.id" :value="cls.id" :label="cls.name + ' - ' + cls.id")
					.classes__footer
						.classes__footer__left
							el-pagination(background='', layout='prev, pager, next', :current-page.sync='page', :page-size='per_page', :total='filtered.length')
						.classes__footer__right(v-if="paginated && filtered")
							| Showing
							strong &nbsp;{{ paginated.length }}
							| &nbsp;out of a possible
							strong &nbsp;{{ filtered.length }}
							| &nbsp;results
</template>
<script>
import gql from "graphql-tag";
import _ from "lodash";
import Fuse from "fuse.js";
import Bugsnag from "@bugsnag/js";
import AutoEnableClassesButton from "../includes/AutoEnableClassesButton.vue";
export default {
	components: {
		AutoEnableClassesButton
	},
	data() {
		return {
			thirdPartyClasses: [],
			thirdPartyClassesFiltered: [],
			filters: null,
			archiveLoading: false,
			saveLoading: false,
			addAdminLoading: false,
			refetchingClasses: false,
			page: 1,
			per_page: 50,
			optionLimit: 20,
			fields: [
				{
					name: "Name",
					path: "name",
					type: "text"
				},
				{
					name: "Code",
					path: "code",
					type: "text"
				},
				{
					name: "Type",
					path: "type",
					type: "radio",
					options: [
						{
							label: "Class",
							value: "class"
						},
						{
							label: "Registration",
							value: "registration"
						}
					]
				},
				{
					name: "Status",
					path: "status",
					type: "radio",
					options: [
						{
							label: "Create on sync",
							value: "create_on_sync"
						},
						{
							label: "Ignored",
							value: "ignore"
						},
						{
							label: "Missing match",
							value: "Class no longer exists"
						}
					]
				}
			],
			allowedMultipleMatches: ["create_on_sync", "create", "ignore", "Class no longer exists"],
			addAdminError: null,
			addAdminProgress: 0,
			addAdminProgressMessages: ["We're adding you to all classrooms. Please wait."],
			multipleSelection: [],
			wondeClassesLoading: true,
			hasErrors: false,
		};
	},
	computed: {
		saveButtonType() {
			return this.multipleMatches ? "danger" : "primary";
		},

		saveText() {
			if (this.multipleMatches) {
				return "Saving is not possible while a class is assigned multiple times";
			}
			return this.saveLoading ? "Saving..." : "Save matches";
		},

		multipleMatches() {
			return _(this.inputClasses)
				.filter({ action: "match" })
				.reject(item => _.includes(this.allowedMultipleMatches, item.providerId))
				.countBy("providerId")
				.some(qty => qty > 1);
		},

		excessiveCreateNows() {
			return _.filter(this.inputClasses, { providerId: "create" }).length > 3;
		},

		inputClasses() {
			return _(this.classes)
				.reject({ assigned: null })
				.map(cls => {
					return {
						wondeId: cls.id.split("-")[0],
						wondeIdType: cls.type,
						providerId: cls.assigned
					};
				})
				.value();
		},

		filtered() {
			let filtered = _.map(this.classes, row => {
				const foundRecord = _.find(this.matchedClasses, { wondeId: row.id.split("-")[0], wondeIdType: row.type });
				if (foundRecord) {
					row.assigned = foundRecord.providerId;
					row.status = foundRecord.status;
					if (foundRecord.status === "ignored") {
						row.assigned = "ignore";
						row.assignedFilterValue = "ignore";
					} else {
						if (!this.$apollo.loading && !_.isEmpty(this.thirdPartyClasses) && !_.find(this.thirdPartyClasses, item => item.id === foundRecord.providerId)) {
							row.assignedFilterValue = "Class no longer exists";
						}
					}
				} else {
					row.assigned = "create_on_sync";
					row.assignedFilterValue = "create_on_sync";
				} 	
				return row;
			});

			if (this.classes && this.filters) {
				if (this.filters.name) {
					filtered = _.filter(filtered, row => {
						return this.filters.name.filter_type == 'starts_with' ? _.startsWith(row.name.toLowerCase(), this.filters.name.value.toLowerCase()) : row.name.toLowerCase().includes(this.filters.name.value.toLowerCase());
					});
				}
				if (this.filters.code) {
					filtered = _.filter(filtered, row => {
						if (_.isEmpty(row.code)) return false;

						return this.filters.code.filter_type == 'starts_with' ? _.startsWith(row.code.toLowerCase(), this.filters.code.value.toLowerCase()) : row.code.toLowerCase().includes(this.filters.code.value.toLowerCase());
					});
				}
				if (this.filters.type) {
					filtered = _.filter(filtered, { type: this.filters.type.value });
				}
				if (this.filters.status) {
					filtered = _.filter(filtered, (cls) => {
						return cls.assignedFilterValue === this.filters.status.value;
					});
				}
			}
			return filtered;
		},

		paginated() {
			return _.chunk(this.filtered, this.per_page)[this.page - 1];
		},

		classesLoading() {
			if(this.refetchingClasses) return true;
			return _.isEmpty(this.thirdPartyClasses);
		},

		actionLoading() {
			return this.addAdminLoading || this.archiveLoading || this.saveLoading;
		},

		groupsInput() {
			return (this.multipleSelection)
				.filter((cls) => {
					return cls.type === "class"
				})
				.map((cls) => {
					return {
						wondeId: cls.id,
						type: cls.type,
						providerId: "create"
					}
				})
		}
	},
	methods: {
		createIgnore(val) {
			this.filtered.map(classItem => (classItem.assigned = val));
		},
		rowDuplicated(item) {
			return (
				item.assigned &&
				_(this.inputClasses)
					.filter({ providerId: item.assigned })
					.reject(item => _.includes(_.concat(this.allowedMultipleMatches, "archive"), item.providerId))
					.value().length > 1
			);
		},

		tooManyCreateNows(item) {
			return item.assigned && this.excessiveCreateNows && item.assigned === "create";
		},

		change(id) {
			if (this.multipleMatches) {
				this.$message.warning("Notice: This class has been matched multiple times");
			}
			if (this.excessiveCreateNows) {
				this.$message.warning("Notice: Only 3 classes can be created at a time here - to create more you should set them to Create on sync");
			}
		},

		getTPClassOptions(row) {
			// If thirdPartyClasses is empty or still loading, show matchedClasses
			// filtering out the ones with null providerIds to prevent
			// "Loading Google classes..." as one of the options
			if (_.isEmpty(this.thirdPartyClasses)) {
				return _(this.matchedClasses)
					.filter(cls => {
						return !_.isEmpty(cls.providerId)
					})
					.map(cls => {
						return {
							id: cls.providerId,
							name: cls.providerName ? cls.providerName : `Loading ${providerName} classes...`
						}
					})
					.value();
			}

			// The filtered list of third party classes starts with a subset of the full list for better performance
			if (_.isEmpty(this.thirdPartyClassesFiltered) && !_.isEmpty(this.thirdPartyClasses)) {
				this.thirdPartyClassesFiltered = this.thirdPartyClasses.slice(0, this.optionLimit);
			}

			// Ensure any classes that have already been assigned are also in the filtered list
			if (!_.includes(this.allowedMultipleMatches, row.assigned)) {
				if (!_.find(this.thirdPartyClassesFiltered, item => item.id === row.assigned)) {
					// Item is not in filtered list, so let's push it in
					const assignedClass = _.find(this.thirdPartyClasses, item => item.id === row.assigned);
					if (assignedClass) {
						this.thirdPartyClassesFiltered.push(assignedClass);
					}
				}
			}
			return this.thirdPartyClassesFiltered;
		},

		classesFilter(query) {
			const options = {
				keys: ["name"]
			};
			var fuse = new Fuse(this.thirdPartyClasses, options);
			this.thirdPartyClassesFiltered = fuse.search(query).slice(0, this.optionLimit);
		},

		async saveMatches(inputClasses) {
			this.saveLoading = true;
			try {
				await this.$apollo.mutate({
					mutation: gql`
						mutation MatchClasses($input: [MatchClassInput!]!) {
							MatchClasses(input: $input) {
								id
								providerId
								wondeId
								wondeIdType
							}
						}
					`,
					variables: {
						input: inputClasses
					}
				});
			} catch (error) {
				Bugsnag.notify(JSON.stringify(error, null, 2));
				
				if (!this.hasErrors) {
					this.$message.error("Failed to save matches");
				}

				this.hasErrors = true;
			}
		},

		async saveByBatch() {
			this.saveLoading = true;
			// Archiving more than 3 classes is timing out for some schools
			const perPage = 3;
			let curPage = 1;
			let promises = [];
			let chunk = _.chunk(this.inputClasses, perPage)[curPage - 1];
			const shouldRefetch = _.filter(this.inputClasses, (cls) => {
				return ["archive", "create"].includes(cls.providerId);
			}).length > 0;

			const hasCreateOnSyncs = _.filter(this.inputClasses, (cls) => {
				return ["create_on_sync"].includes(cls.providerId);
			}).length > 0;

			if (hasCreateOnSyncs) {
				this.enableAutoSyncClasses();
			}

			
			while(chunk) {
				promises.push(this.saveMatches(chunk));
				curPage++;
				chunk = _.chunk(this.inputClasses, perPage)[curPage - 1];
			}

			await Promise.all(promises);
			await this.$apollo.queries.matchedClasses.refetch();
			
			// Clear cache if there are archived or newly created classes
			if (shouldRefetch) {
				this.$message.success("Class matches saved successfully. Please wait while classes are re-fetched");
				await this.clearClassesCache();
			} else {
				this.$message.success("Class matches saved successfully");
			}

			this.saveLoading = false;
		},

		async archiveClasses() {
			this.$confirm(
				`This will archive ALL CLASSES that have been matched, not just those marked for archiving below. Are you sure you want to proceed?`,
				"Warning",
				{
					confirmButtonText: "OK",
					cancelButtonText: "Cancel",
					type: "warning"
				}
			).then(async () => {
				try {
					this.archiveLoading = true;
					
					// Set all matched classes to archive then save
					this.classes = _(this.classes)
						.map(cls => {
							if (/^\d+$/.test(cls.assigned) || !["ignore", "create_on_sync", "create", "archive"].includes(cls.assigned)) {
								cls.assigned = "archive";
							}
							return cls;
						})
						.value();
					
					await this.saveByBatch();
					await this.$apollo.queries.matchedClasses.refetch();
					// await this.$apollo.queries.thirdPartyClasses.refetch();
					await this.clearClassesCache();
					this.archiveLoading = false;
				} catch (error) {
					this.archiveLoading = false;
					Bugsnag.notify(JSON.stringify(error, null, 2));
					this.$message.error("Failed to archive classes");
				}
			});
		},

		async addAdminToAllClassesByBatch() {
			if (!this.classUser.provider_id) {
				const confirmed = await this.$confirm(
					`In order to add you to all Google classrooms, we need to obtain your ID from Google by reconnecting your account to your Google Workspace for Education. Do you want to proceed?`,
					"Warning",
					{
						confirmButtonText: "Ok",
						cancelButtonText: "Cancel",
						type: "warning"
					}
				)
				
				if (confirmed) {
					this.$router.push({ name: "connect.reconnect" });
				} 
			} else {
				this.addAdminLoading = true;
				this.addAdminProgress = 0;
				let curPage = 1;
				const matches = _.filter(this.matchedClasses, (cls) => {
					return cls.status == "matched"
				});
				const perPage = 10;
				let chunk = _.chunk(matches, perPage)[curPage - 1];
				const maxPage = Math.ceil(matches.length / perPage);
				
				while(chunk) {
					await this.addAdminToAllClasses(chunk);
					this.addAdminProgress = Math.floor((curPage / maxPage) * 100);
					curPage++;
					chunk = _.chunk(matches, perPage)[curPage - 1];
				}
				
				if (this.addAdminError) {
					this.$message.error(this.addAdminError.message);
				} else {
					this.$message.success("You have been successfully added to all classrooms");
				}
		
				this.addAdminLoading = false;
			}
		},

		async addAdminToAllClasses(classes) {
			try {
				await this.$apollo.mutate({
					mutation: gql`
							mutation AddUserToAllClasses($classMatches: [MatchClassInput!]) {
								AddUserToAllClasses(classMatches: $classMatches)
							}
						`,
						variables: {
							classes
						}
				});
			} catch (error) {
				this.addAdminError = error;
				Bugsnag.notify(JSON.stringify(error, null, 2));
			}
		},
		
		async refetchClasses() {
			this.$confirm(
				`Re-fetching classes will take some time. Are you sure you want to proceed?`,
				"Warning",
				{
					confirmButtonText: "OK",
					cancelButtonText: "Cancel",
					type: "warning"
				}
			).then(async () => {
				await this.clearClassesCache();
			})
		},

		async clearClassesCache() {
			try {
				await this.$apollo.mutate({
					mutation: gql`
						mutation CacheClear($item: CacheTypeEnum!){
							CacheClear(item: $item)
						}`,
						variables: {
							item: "classes"
						}
				});
				this.refetchingClasses = true;
				this.thirdPartyClasses = [];
				this.thirdPartyClassesFiltered = [];
				await this.$apollo.queries.thirdPartyClasses.refetch();
			} catch (e) {
				Bugsnag.notify(JSON.stringify(e, null, 2));
				this.$message.error("Failed to re-fetch classes");
			}
		},

		refetchMathedClasses() {
			this.$apollo.queries.matchedClasses.refetch();
		},

		actionCommand(command) {
			switch (command) {
				case "add_admin":
					this.addAdminToAllClassesByBatch();
					break;
				case "archive":
					this.archiveClasses();
					break;
				case "refetch":
					this.refetchClasses();
					break;
				case "create_group":
					this.createGroups()
					break;
				default:
					this.saveByBatch()
			}
		},

		handleSelectionChange(val) {
			this.multipleSelection = val;
		},

		async createGroups() {
			this.saveLoading = true;
			try {
				await this.$apollo.mutate({
					mutation: gql`
						mutation MatchGroups($input: [MatchGroupInput!]!) {
							MatchGroups(input: $input) {
								id
								type
								name
							}
						}
					`,
					variables: {
						input: this.groupsInput
					}
				});
				this.$message.success("Groups have been created successfully")
			} catch (error) {
				Bugsnag.notify(JSON.stringify(error, null, 2));
				this.$message.error("Failed to create groups");
			}
			this.saveLoading = false;
		},

		async enableAutoSyncClasses() {
			this.$refs.classEnableBtn.setEnabled(true);
		}
	},

	apollo: {
		matchedClasses: {
			query: gql`
				{
					MatchedClasses {
						id
						status
						providerId
						wondeId
						wondeIdType
						providerName
					}
				}
			`,
			fetchPolicy: "no-cache",
			update: r => r.MatchedClasses
		},

		thirdPartyClasses: {
			query: gql`
				query ThirdPartyClasses {
					ThirdPartyClasses {
						id
						name
					}
				}
			`,
			fetchPolicy: "no-cache",
			update(r) {
				if(r.ThirdPartyClasses && r.ThirdPartyClasses.length > 0) {
					this.refetchingClasses = false;
				}
				return r.ThirdPartyClasses;
			},
			pollInterval: 5000,
			error(e) {
				Bugsnag.notify(JSON.stringify(e, null, 2));
				this.$message.closeAll();
				this.$message({
					type: "error",
					message: `Something went wrong. Please refresh the page.`,
					duration: 0,
					showClose: true
				});
			},
			skip() {
				return !this.classesLoading;
			}
		},

		classes: {
			query: gql`
				{
					Classes {
						id
						name
						type
						code
					}
				}
			`,
			fetchPolicy: "no-cache",
			update(r) {
				this.wondeClassesLoading = false;

				return _(r.Classes)
					.map(cls => ({
						...cls,
						assigned: null
					}))
					.sortBy("name")
					.value()
			}
		},

		classSettings: {
			query: gql`
				{
					Settings {
						id
						class_create_mode
						class_membership_mode
					}
				}
			`,
			fetchPolicy: "no-cache",
			update(r) {
				return _.cloneDeep(r.Settings);
			}
		},

		classUser: {
			query: gql`
				{
					User {
						provider_id
				}
			}
			`,
			fetchPolicy: "cache-and-network",
			update: (r) => r.User,
		}
	}
};
</script>
